import React, { useCallback } from "react"
import { Button, Select, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faXmark } from "@fortawesome/free-solid-svg-icons"
import { useQueryBuilderStore } from "stores/queryBuilderStore"

interface BuilderTabsProps {
  /** Optional override for delete: when provided, the container handles the
   *  backend round-trip (delete the saved record if any) and the local store
   *  removal. Without it, the X button only removes locally. */
  onDeleteActiveBuilder?: () => Promise<void>
}

/**
 * Active-builder selector. A compact dropdown with + and × controls. Each
 * builder still owns its own configuration and result; the dropdown just
 * picks which one is currently visible.
 *
 * Removing the last builder auto-creates a fresh one so the workspace is
 * never empty.
 */
const BuilderTabs: React.FC<BuilderTabsProps> = ({ onDeleteActiveBuilder }) => {
  const builders = useQueryBuilderStore((s) => s.builders)
  const activeId = useQueryBuilderStore((s) => s.activeBuilderId)
  const setActive = useQueryBuilderStore((s) => s.setActiveBuilder)
  const create = useQueryBuilderStore((s) => s.createBuilder)
  const remove = useQueryBuilderStore((s) => s.removeBuilder)

  const handleChange = useCallback(
    (id: string) => setActive(id),
    [setActive]
  )

  const handleAdd = useCallback(() => {
    create()
  }, [create])

  const handleRemove = useCallback(() => {
    if (!activeId) return
    if (onDeleteActiveBuilder) {
      onDeleteActiveBuilder().catch((err) => {
        console.error("Builder delete failed:", err)
      })
    } else {
      remove(activeId)
    }
  }, [activeId, remove, onDeleteActiveBuilder])

  if (builders.length === 0) return null

  return (
    <div className="qc-builder-bar" role="group" aria-label="Builder">
      <span className="qc-builder-bar__label">Builder:</span>
      <Select
        size="small"
        className="qc-builder-bar__select"
        value={activeId ?? undefined}
        onChange={handleChange}
        options={builders.map((b) => ({ value: b.id, label: b.name }))}
        popupMatchSelectWidth={false}
      />
      <Tooltip title="Add builder" placement="bottom">
        <Button
          aria-label="Add builder"
          icon={<FontAwesomeIcon icon={faPlus} />}
          onClick={handleAdd}
        />
      </Tooltip>
      <Tooltip title="Delete current builder" placement="bottom">
        <Button
          danger
          aria-label="Delete current builder"
          icon={<FontAwesomeIcon icon={faXmark} />}
          onClick={handleRemove}
          disabled={!activeId}
        />
      </Tooltip>
    </div>
  )
}

export default BuilderTabs
